import { APIError, getAPIBaseURL, requestJSON, type AnalyticsRequestInit } from '@/lib/api/api';
import type {
  AuthResult,
  LoginRequest,
  MeResult,
  PatchMeRequest,
  ProfileVisibility,
  ProfileResponse,
  RefreshRequest,
  SignupRequest,
  UpdatePrivacyRequest,
} from '@/lib/api/apiTypes';
import {
  flushPostHog,
  identifyPostHog,
  setPostHogFeatureFlagTraits,
  resetPostHog,
  setAnalyticsMirrorAccessToken,
  trackAccountCreated,
  trackLogin,
  trackLogout,
} from '@/lib/analytics/posthog';
import { Platform } from 'react-native';
import { clearStoredSession, readStoredSession, writeStoredSession } from '@/lib/auth/sessionStorage';
import { isRefreshSessionCurrent } from '@/lib/auth/authRefresh';
import { unregisterPushTokenBeforeLogout } from '@/lib/auth/logoutPushToken';
import { scheduleTimeout } from '@/lib/app/nativeEventSubscriptions';
import { captureSentryException, setSentryUser } from '@/lib/analytics/sentry';
import { canSyncPreciseLocation } from '@/lib/location/locationPrivacy';
import * as React from 'react';
import { create } from 'zustand';
import Constants from 'expo-constants';

const APP_VERSION = Constants.nativeAppVersion ?? Constants.expoConfig?.version ?? '0.0.0';
const LOGOUT_PUSH_CLEANUP_TIMEOUT_MS = 3_000;

async function finishLogoutCleanup(session: SessionState, analyticsFlush: Promise<void>) {
  try {
    const cleanupController = new AbortController();
    const cancelCleanupTimeout = scheduleTimeout(
      () => cleanupController.abort(),
      LOGOUT_PUSH_CLEANUP_TIMEOUT_MS
    );
    try {
      const { getCurrentExpoPushToken } = await import('@/lib/notifications/notifications');
      await unregisterPushTokenBeforeLogout({
        getToken: () => getCurrentExpoPushToken(LOGOUT_PUSH_CLEANUP_TIMEOUT_MS),
        unregisterToken: (token) =>
          requestJSON(
            '/me/push-token',
            {
              method: 'DELETE',
              body: JSON.stringify({ token }),
              signal: cleanupController.signal,
            },
            session.accessToken
          ),
      });
    } finally {
      cancelCleanupTimeout();
    }

    const payload: RefreshRequest = { refresh_token: session.refreshToken };
    await requestJSON('/auth/logout', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    await analyticsFlush;
  } catch (error) {
    captureSentryException(error, {
      tags: { area: 'auth', kind: 'logout_cleanup' },
    });
  } finally {
    resetPostHog();
  }
}

type SessionState = {
  accessToken: string;
  refreshToken: string;
  me: MeResult;
  emailVerified: boolean;
};

type AuthState = {
  session: SessionState | null;
  isBusy: boolean;
  isHydrating: boolean;
  hydrate: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  signup: (input: {
    email: string;
    password: string;
    username: string;
    displayName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  request: <T>(path: string, init?: AnalyticsRequestInit) => Promise<T>;
  requestRaw: (path: string, init?: AnalyticsRequestInit) => Promise<Response>;
  setProfileVisibility: (visibility: ProfileVisibility) => Promise<void>;
  updateLocationTrackingEnabled: (enabled: boolean) => Promise<void>;
  refreshMe: () => Promise<void>;
  syncMarketLocation: (lat: number, lng: number) => Promise<MeResult | null>;
  deleteAccount: () => Promise<void>;
  setEmailVerified: (value: boolean) => void;
  setMe: (me: MeResult) => void;
};

let hydratePromise: Promise<void> | null = null;
// Single-flight refresh keeps concurrent 401s from rotating the session out from under each other.
let refreshPromise: Promise<SessionState> | null = null;

function normalizeSignupUsername(username: string) {
  return username.trim().replace(/@/g, '');
}

function normalizeEmailVerified(emailVerified: boolean) {
  if (__DEV__) {
    return true;
  }

  return emailVerified;
}

export function toSession(result: AuthResult): SessionState {
  return {
    accessToken: result.access_token,
    refreshToken: result.refresh_token,
    me: {
      user: result.user,
      profile: result.profile,
      email_verified: result.email_verified,
      email_delivery_failed: result.email_delivery_failed,
    },
    emailVerified: normalizeEmailVerified(result.email_verified),
  };
}

export function verifyEmailToken(token: string) {
  return requestJSON<AuthResult>('/auth/email/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export function authenticateWithApple(input: {
  identityToken: string;
  fullName?: string | null;
  email?: string | null;
}) {
  return requestJSON<AuthResult>('/auth/apple', {
    method: 'POST',
    body: JSON.stringify({
      identity_token: input.identityToken,
      full_name: input.fullName,
      email: input.email,
    }),
  });
}

export function authenticateWithGoogle(idToken: string) {
  return requestJSON<AuthResult>('/auth/google', {
    method: 'POST',
    body: JSON.stringify({ id_token: idToken }),
  });
}

function normalizeStoredSession(
  stored: Awaited<ReturnType<typeof readStoredSession>>
): SessionState | null {
  if (!stored) {
    return null;
  }

  return {
    accessToken: stored.accessToken,
    refreshToken: stored.refreshToken,
    me: stored.me,
    emailVerified: normalizeEmailVerified((stored as any).emailVerified ?? false),
  };
}

export const authStore = create<AuthState>((set, get) => ({
  session: null,
  isBusy: false,
  isHydrating: true,
  async hydrate() {
    if (!hydratePromise) {
      hydratePromise = readStoredSession()
        .then((stored) => {
          set({
            session: normalizeStoredSession(stored),
            isHydrating: false,
          });
        })
        .catch(() => {
          set({
            session: null,
            isHydrating: false,
          });
        })
        .finally(() => {
          hydratePromise = null;
        });
    }

    return hydratePromise;
  },
  async login(email: string, password: string) {
    set({ isBusy: true });
    try {
      const payload: LoginRequest = { email, password };
      const result = await requestJSON<AuthResult>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const session = toSession(result);
      set({ session });
      setAnalyticsMirrorAccessToken(session.accessToken);
      identifyPostHog({
        userID: session.me.user.id,
        email: session.me.user.email,
        username: session.me.profile.username,
      });
      trackLogin('email');
    } finally {
      set({ isBusy: false });
    }
  },
  async requestPasswordReset(email: string) {
    set({ isBusy: true });
    try {
      await requestJSON('/auth/password/reset/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } finally {
      set({ isBusy: false });
    }
  },
  async resetPassword(token: string, password: string) {
    set({ isBusy: true });
    try {
      await requestJSON('/auth/password/reset/confirm', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
    } finally {
      set({ isBusy: false });
    }
  },
  async signup(input) {
    set({ isBusy: true });
    try {
      const username = normalizeSignupUsername(input.username);
      const payload: SignupRequest = {
        email: input.email,
        password: input.password,
        username,
        display_name: input.displayName,
      };
      const result = await requestJSON<AuthResult>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const session = toSession(result);
      set({ session });
      setAnalyticsMirrorAccessToken(session.accessToken);
      identifyPostHog({
        userID: session.me.user.id,
        email: session.me.user.email,
        username: session.me.profile.username,
      });
      trackAccountCreated('email');
    } finally {
      set({ isBusy: false });
    }
  },
  async logout() {
    const currentSession = get().session;
    if (!currentSession) {
      return;
    }

    set({ isBusy: true });
    try {
      await clearStoredSession();
      trackLogout();
      const analyticsFlush = flushPostHog().catch((error) => {
        captureSentryException(error, {
          tags: { area: 'analytics', kind: 'logout_flush' },
        });
      });
      set({ session: null, isBusy: false });
      setAnalyticsMirrorAccessToken(null);
      void finishLogoutCleanup(currentSession, analyticsFlush);
    } catch (error) {
      set({ isBusy: false });
      throw error;
    }
  },
  async request<T>(path: string, init: AnalyticsRequestInit = {}) {
    const currentSession = get().session;

    try {
      return await requestJSON<T>(path, init, currentSession?.accessToken);
    } catch (error) {
      if (!(error instanceof APIError) || error.status !== 401 || !currentSession?.refreshToken) {
        throw error;
      }

      const latestSession = get().session;
      if (latestSession?.accessToken && latestSession.accessToken !== currentSession.accessToken) {
        return requestJSON<T>(path, init, latestSession.accessToken);
      }

      if (!latestSession?.refreshToken) {
        throw error;
      }

      if (!refreshPromise) {
        const payload: RefreshRequest = { refresh_token: latestSession.refreshToken };
        refreshPromise = requestJSON<AuthResult>('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
          .then((result) => {
            if (!isRefreshSessionCurrent(get().session?.refreshToken, latestSession.refreshToken)) {
              throw new Error('Session changed during token refresh');
            }
            const nextSession = toSession(result);
            set({ session: nextSession });
            return nextSession;
          })
          .catch((refreshError) => {
            // Only clear auth state when the refresh token is actually rejected.
            // Network hiccups or temporary backend failures should not sign the user out.
            if (
              refreshError instanceof APIError &&
              (refreshError.status === 401 || refreshError.status === 403) &&
              isRefreshSessionCurrent(get().session?.refreshToken, latestSession.refreshToken)
            ) {
              set({ session: null });
              resetPostHog();
            }
            throw refreshError;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      const nextSession = await refreshPromise;
      return requestJSON<T>(path, init, nextSession.accessToken);
    }
  },
  async requestRaw(path: string, init: AnalyticsRequestInit = {}) {
    const currentSession = get().session;

    const perform = (accessToken?: string | null) => {
      const headers = new Headers(init.headers ?? {});
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }
      const { analyticsAction: _analyticsAction, ...requestInit } = init;
      return fetch(`${getAPIBaseURL()}${path}`, {
        ...requestInit,
        headers,
      });
    };

    let response = await perform(currentSession?.accessToken);
    if (response.status !== 401 || !currentSession?.refreshToken) {
      return response;
    }

    const latestSession = get().session;
    if (latestSession?.accessToken && latestSession.accessToken !== currentSession.accessToken) {
      return perform(latestSession.accessToken);
    }

    if (!latestSession?.refreshToken) {
      return response;
    }

    if (!refreshPromise) {
      const payload: RefreshRequest = { refresh_token: latestSession.refreshToken };
      refreshPromise = requestJSON<AuthResult>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
        .then((result) => {
          if (!isRefreshSessionCurrent(get().session?.refreshToken, latestSession.refreshToken)) {
            throw new Error('Session changed during token refresh');
          }
          const nextSession = toSession(result);
          set({ session: nextSession });
          return nextSession;
        })
        .catch((refreshError) => {
          if (
            refreshError instanceof APIError &&
            (refreshError.status === 401 || refreshError.status === 403) &&
            isRefreshSessionCurrent(get().session?.refreshToken, latestSession.refreshToken)
          ) {
            set({ session: null });
            resetPostHog();
          }
          throw refreshError;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    const nextSession = await refreshPromise;
    response = await perform(nextSession.accessToken);
    return response;
  },
  async setProfileVisibility(visibility: ProfileVisibility) {
    if (!get().session) {
      return;
    }

    const body: PatchMeRequest = { profile_visibility: visibility };
    const payload = await get().request<ProfileResponse>('/me', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const latestSession = get().session;
    if (!latestSession) {
      return;
    }

    set({
      session: {
        ...latestSession,
        me: {
          ...latestSession.me,
          profile: {
            ...latestSession.me.profile,
            ...payload.profile,
          },
        },
      },
    });
  },
  async updateLocationTrackingEnabled(enabled: boolean) {
    if (!get().session) {
      return;
    }

    const body: UpdatePrivacyRequest = { location_tracking_enabled: enabled };
    const me = await get().request<MeResult>('/me/privacy', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    const latestSession = get().session;
    if (!latestSession) {
      return;
    }

    set({
      session: {
        ...latestSession,
        me,
        emailVerified: normalizeEmailVerified(me.email_verified),
      },
    });
  },
  async deleteAccount() {
    if (!get().session) {
      return;
    }

    set({ isBusy: true });
    try {
      await get().request('/me', { method: 'DELETE' });
      trackLogout();
      await flushPostHog();
    } finally {
      set({ session: null, isBusy: false });
      resetPostHog();
    }
  },
  async refreshMe() {
    if (!get().session) {
      return;
    }

    const me = await get().request<MeResult>('/me');
    const latestSession = get().session;
    if (!latestSession) {
      return;
    }

    set({
      session: {
        ...latestSession,
        me,
        emailVerified: normalizeEmailVerified(me.email_verified),
      },
    });
  },
  async syncMarketLocation(lat: number, lng: number) {
    if (!canSyncPreciseLocation(get().session?.me.profile ?? {})) {
      return null;
    }

    const me = await get().request<MeResult>('/me/location', {
      method: 'POST',
      body: JSON.stringify({ lat, lng }),
    });
    const latestSession = get().session;
    if (!latestSession) {
      return null;
    }

    set({
      session: {
        ...latestSession,
        me,
        emailVerified: normalizeEmailVerified(me.email_verified),
      },
    });

    return me;
  },
  setEmailVerified(value: boolean) {
    const latestSession = get().session;
    if (!latestSession) {
      return;
    }

    set({
      session: {
        ...latestSession,
        me: {
          ...latestSession.me,
          email_verified: value,
        },
        emailVerified: normalizeEmailVerified(value),
      },
    });
  },
  setMe(me) {
    const latestSession = get().session;
    if (!latestSession) {
      return;
    }

    set({
      session: {
        ...latestSession,
        me,
        emailVerified: normalizeEmailVerified(me.email_verified),
      },
    });
  },
}));

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const hydrate = authStore((state) => state.hydrate);
  const session = authStore((state) => state.session);
  const isHydrating = authStore((state) => state.isHydrating);

  React.useEffect(() => {
    hydrate().catch(() => undefined);
  }, [hydrate]);

  React.useEffect(() => {
    if (isHydrating) {
      return;
    }

    if (session) {
      writeStoredSession(session).catch(() => undefined);
      return;
    }

    clearStoredSession().catch(() => undefined);
  }, [session, isHydrating]);

  React.useEffect(() => {
    if (isHydrating) {
      return;
    }

    if (!session) {
      setAnalyticsMirrorAccessToken(null);
      setSentryUser(null);
      return;
    }

    setAnalyticsMirrorAccessToken(session.accessToken);
    identifyPostHog({
      userID: session.me.user.id,
      email: session.me.user.email,
      username: session.me.profile.username,
    });
    setPostHogFeatureFlagTraits({
      appVersion: APP_VERSION,
      hasClaimedVenue: Boolean(session.me.profile.is_venue && session.me.profile.venue_id),
      isStaff: session.me.profile.is_staff,
      platform: Platform.OS,
    });
    setSentryUser({
      id: session.me.user.id,
      username: session.me.profile.username || null,
    });
  }, [isHydrating, session]);

  return <>{children}</>;
}

export function useAuth(): AuthState;
export function useAuth<T>(selector: (state: AuthState) => T): T;
export function useAuth<T>(selector?: (state: AuthState) => T) {
  return selector ? authStore(selector) : authStore();
}
